'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Finance, Interfaces } from 'doodle-icons';
import StationeryCapabilityBoard, { type StationeryCapabilityPanel } from '@/components/common/StationeryCapabilityBoard';
import { trackEvent } from '@/lib/analytics';

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const PROBLEMS = [
  { cat: 'Workload', title: '10\u201320 hrs/week', body: 'Per teacher, spent checking papers instead of teaching.', rotation: '-0.5deg' },
  { cat: 'Visibility', title: 'Marks, not mastery', body: 'Gradebooks show scores. Not which concept each student struggles with.', rotation: '0.6deg' },
  { cat: 'Consistency', title: 'Teacher-dependent', body: 'Feedback quality varies across teachers and departments.', rotation: '-0.3deg' },
  { cat: 'Parents', title: 'No concept data', body: 'Parent meetings backed by impressions, not mastery evidence.', rotation: '0.5deg' },
];

const FEATURE_PANELS: StationeryCapabilityPanel[] = [
  {
    eyebrow: 'Observe',
    title: 'School-wide concept visibility.',
    description: 'Leadership can finally see which concepts are landing across departments, teachers, and boards.',
    icon: <Finance.TrendUp width={22} height={22} fill="currentColor" />,
    accent: 'blue',
    rotation: '-0.6deg',
    points: [
      {
        label: 'School-Wide Concept Analytics',
        detail: 'Track mastery across classes, subjects, and teachers from one operating view.',
        icon: <Finance.TrendUp width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Concept Gap Detection',
        detail: 'Flag patterns where a whole class or stream keeps failing the same prerequisite.',
        icon: <Interfaces.Caution width={18} height={18} fill="currentColor" />,
      },
    ],
  },
  {
    eyebrow: 'Adopt',
    title: 'Fit the way schools already operate.',
    description: 'The system is designed to slip into existing teacher workflows instead of demanding a new process.',
    icon: <Interfaces.Folder width={22} height={22} fill="currentColor" />,
    accent: 'green',
    rotation: '0.8deg',
    points: [
      {
        label: 'CBSE / ICSE Aligned',
        detail: 'Upload board-specific worksheets and keep the mapping tied to the concepts teachers already teach.',
        icon: <Interfaces.Folder width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Teacher-ready onboarding',
        detail: 'Upload, review, and start grading without extra training overhead.',
        icon: <Interfaces.Zap width={18} height={18} fill="currentColor" />,
      },
    ],
  },
  {
    eyebrow: 'Communicate',
    title: 'Turn reporting into evidence, not guesswork.',
    description: 'Parents and school leaders get a clearer picture because feedback is tied to mastery rather than only marks.',
    icon: <Interfaces.Doc width={22} height={22} fill="currentColor" />,
    accent: 'amber',
    rotation: '-0.25deg',
    points: [
      {
        label: 'Parent Intelligence Reports',
        detail: 'Explain real progress in concept language before the parent meeting starts.',
        icon: <Interfaces.Doc width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Happier teachers',
        detail: 'Teachers recover grading time and keep the quality of feedback consistent.',
        icon: <Interfaces.Shield width={18} height={18} fill="currentColor" />,
      },
    ],
  },
];

const FAQS = [
  {
    q: 'How does IntelGrader work across multiple departments?',
    a: 'Each teacher uploads their own worksheets and rubrics. School leaders get a unified dashboard showing concept mastery across all classes, subjects, and teachers.',
  },
  {
    q: 'How long does onboarding take?',
    a: 'Teachers start in minutes. Upload a worksheet, set a rubric, and grade. No training sessions needed.',
  },
  {
    q: 'What boards and curricula are supported?',
    a: 'CBSE, ICSE, State Boards, GCSE, A-Level, and any custom curriculum. If you can define a rubric, IntelGrader can grade against it.',
  },
];

const SchoolsPage: React.FC = () => {
  const hero = useReveal(0.1);
  const problem = useReveal();
  const features = useReveal();
  const faq = useReveal();

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section ref={hero.ref} className="page-hero page-hero--schools py-24 md:py-32 px-6 text-center relative overflow-hidden" style={{ background: 'var(--color-bg)' }}>
        <div className={`max-w-3xl mx-auto reveal-fade-up ${hero.visible ? 'is-revealed' : ''}`}>
          <span className="section-eyebrow">For Schools</span>
          <h1 className="section-title mt-2">Data-driven teaching. Happier teachers.</h1>
          <p className="section-subtitle mx-auto mt-4" style={{ maxWidth: 520 }}>
            Give your teachers back 10&ndash;20 hours a week while gaining
            school-wide concept mastery data across every class, subject, and board.
          </p>
          <div className="flex gap-3 justify-center flex-wrap mt-8">
            <a href="/book-demo" onClick={() => trackEvent('book_demo_click', 'CTA', 'schools_hero')} className="btn btn-primary btn-lg">Book a Demo &rarr;</a>
            <a href="/#how-it-works" className="btn btn-secondary btn-lg">See How It Works</a>
          </div>
        </div>
      </section>

      {/* Problem (green sticky notes — school-leader color) */}
      <section ref={problem.ref} className="py-24 px-6">
        <div className={`max-w-[1250px] mx-auto reveal-fade-up ${problem.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">The Problem</span>
            <h2 className="section-title">Your teachers grade for hours. Your school sees only marks.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            {PROBLEMS.map((p, i) => (
              <div
                key={p.cat}
                className="sticky-note"
                style={{
                  '--sticky-bg': '#e0f7ec',
                  '--sticky-tape': 'rgba(50,151,87,0.18)',
                  '--sticky-icon-color': '#329757',
                  transform: `rotate(${p.rotation})`,
                  animationDelay: `${i * 150}ms`,
                } as React.CSSProperties}
              >
                <div className="sticky-note__tape" />
                <div className="sticky-note__cat" style={{ color: '#329757' }}>{p.cat}</div>
                <h3 className="sticky-note__title">{p.title}</h3>
                <p className="sticky-note__body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section ref={features.ref} className="market-page-band py-24 px-6" style={{ background: 'var(--color-bg)' }}>
        <div className={`max-w-[1250px] mx-auto reveal-fade-up ${features.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">What Changes</span>
            <h2 className="section-title">From report cards to concept maps.</h2>
          </div>
          <div className="mt-8">
            <StationeryCapabilityBoard panels={FEATURE_PANELS} variant="trio" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faq.ref} className="py-24 px-6">
        <div className={`max-w-3xl mx-auto reveal-fade-up ${faq.visible ? 'is-revealed' : ''}`}>
          <div className="section-header section-header--compact">
            <span className="section-eyebrow">FAQ</span>
            <h2 className="section-title">Common questions from school leaders.</h2>
          </div>
          <div className="mt-8 space-y-4">
            {FAQS.map((item) => (
              <div key={item.q} className="glass-card p-6">
                <h3 className="text-base font-semibold text-[var(--color-text)] mb-2">{item.q}</h3>
                <p className="text-sm text-[var(--color-muted)] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div
          className="max-w-[1250px] mx-auto text-center py-20 px-8 relative overflow-hidden"
          style={{ background: 'linear-gradient(175deg, #428fe9, #3475db)', borderRadius: 24 }}
        >
          <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight">
            See it work with your school.
          </h2>
          <p className="text-white/70 text-lg mt-3" style={{ fontFamily: 'var(--font-caveat)' }}>
            Upload a worksheet. Get concept-mapped feedback in seconds.
          </p>
          <div className="mt-8">
            <a href="/book-demo" onClick={() => trackEvent('book_demo_click', 'CTA', 'schools_bottom')} className="btn btn-lg bg-white text-[#428fe9] hover:bg-white/90 font-bold">
              Book a Demo &rarr;
            </a>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          }),
        }}
      />
    </div>
  );
};

export default SchoolsPage;
